import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../contexts/UserContext";
import { useAchievements } from "../contexts/AchievementContext";
import apiService from "../services/api";
import { calculateLevel, calculateProgress } from "../utils/levelSystem";
import "../styles/Dashboard.css";

const Dashboard = ({ sidebarOpen }) => {
  const navigate = useNavigate();
  const { user, updateUser } = useUser();
  const { isShowingAchievement, handleApiResponse } = useAchievements();
  const [stats, setStats] = useState({ streak: 0, points: 0, hasCompletedToday: false });
  const [newAchievements, setNewAchievements] = useState([]);
  const [dailyBonusClaimed, setDailyBonusClaimed] = useState(false);
  const [nextBonusTime, setNextBonusTime] = useState('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [todaysTip, setTodaysTip] = useState('');
  const [todaysFact, setTodaysFact] = useState('');
  const [selectedEarnActivity, setSelectedEarnActivity] = useState('');
  const [leaderboard, setLeaderboard] = useState([]);
  const [hasActiveShield, setHasActiveShield] = useState(false);
  const [canSpinWheel, setCanSpinWheel] = useState(true);
  const [completedTasks, setCompletedTasks] = useState({
    meal_plan: false,
    pantry_update: false,
    shopping_create: false,
    shopping_complete: false,
    feed_post: false,
    feed_comment: false,
    feed_like: false,
    sous_chef_chat: false,
    profile_bio: false,
    profile_picture: false
  });
  
  // Calculate level and progress
  const currentLevel = calculateLevel(stats.points);
  const progress = calculateProgress(stats.points);

  // Daily cooking tips array
  const cookingTips = useMemo(() => [
    "Always read the entire recipe before you start cooking.",
    "Taste your food as you go—season gradually, don't dump at once.",
    "Let meat rest after cooking so juices redistribute.",
    "Save pasta water—it makes sauces silky and flavorful.",
    "Toast spices before using for richer flavor.",
    "Keep your knife sharp—it's safer than a dull blade.",
    "Don't overcrowd the pan; it lowers the heat and food steams instead of browns.",
    "Add salt to boiling water before pasta or veggies—it enhances flavor.",
    "Deglaze pans with a splash of wine or broth for instant sauce.",
    "Use lemon juice to brighten up any dull-tasting dish.",
    "Store grains, pasta, and flour in airtight containers to prevent pests.",
    "Write purchase dates on food packages to track freshness.",
    "Keep herbs fresher by storing them in a jar of water, like flowers.",
    "Rotate pantry items—new purchases go to the back.",
    "Freeze bread before it goes stale; toast slices when needed.",
    "Keep onions and potatoes separate—they spoil faster together.",
    "Store ripe bananas in the fridge to slow browning (the peel darkens, but fruit is fine).",
    "Use transparent containers so you can see ingredients easily.",
    "Keep a \"use soon\" box in your fridge for items near expiry.",
    "Don't wash berries until just before eating—they mold faster.",
    "Save veggie scraps to make homemade stock.",
    "Freeze leftover herbs in olive oil in ice cube trays.",
    "Turn stale bread into croutons or breadcrumbs.",
    "Regrow scallions or lettuce from their roots in water.",
    "Freeze ripe bananas for smoothies or baking.",
    "Use citrus peels to flavor water, vinegar, or sugar.",
    "Plan weekly meals around what you already have.",
    "Repurpose leftovers into wraps, soups, or fried rice.",
    "Share extra food with friends, neighbors, or local charities.",
    "Compost food scraps instead of throwing them away.",
    "Make a shopping list and stick to it to avoid waste.",
    "Don't shop while hungry—you'll buy more than needed.",
    "Buy in bulk only if you'll use it before it expires.",
    "Plan meals with overlapping ingredients to use everything.",
    "Shop seasonal produce—it's cheaper and fresher.",
    "Learn the difference between \"best before\" and \"use by\" dates.",
    "Freeze meat in smaller portions to avoid defrosting too much at once.",
    "Store milk at the back of the fridge where it's coldest.",
    "Try a \"pantry challenge\" once a month to clear old items.",
    "Buy frozen veggies—they last longer and are just as nutritious.",
    "Cook once, eat twice—make extra for leftovers.",
    "Batch cook grains like rice or quinoa and use them all week.",
    "Keep a kitchen notebook of recipes, tips, and family favorites.",
    "Invest in good storage containers to keep food fresher longer.",
    "Label freezer items with name + date.",
    "Cook with all parts of a vegetable (like broccoli stems).",
    "Drink more water flavored with leftover fruit slices.",
    "Involve kids or family in cooking—it reduces waste and builds skills.",
    "Learn quick \"base recipes\" (soups, stir-fry, pasta) to adapt with whatever you have.",
    "Celebrate \"leftovers night\" once a week to clean out the fridge."
  ], []);

  // Fun food facts array
  const foodFacts = useMemo(() => [
    "Carrots were originally purple, not orange.",
    "Honey never spoils — archaeologists found edible honey in 3,000-year-old tombs.",
    "Bananas are berries, but strawberries are not.",
    "Ketchup was once sold as medicine in the 1830s.",
    "Apples float in water because they are 25% air.",
    "Potatoes were the first food grown in space.",
    "Peanuts aren't nuts — they're legumes.",
    "The most expensive coffee is made from beans eaten and passed by a civet.",
    "Broccoli contains more vitamin C than an orange.",
    "Cucumbers are 95% water.",
    "The word \"chocolate\" comes from the Aztec word xocoatl.",
    "Cheese is the most stolen food in the world.",
    "Arachibutyrophobia is the fear of peanut butter sticking to the roof of your mouth.",
    "Pineapples take about two years to grow.",
    "Tomatoes were once thought to be poisonous in Europe.",
    "Cotton candy was invented by a dentist.",
    "The popsicle was invented by an 11-year-old.",
    "Almonds are seeds, not true nuts.",
    "Coffee beans are actually the seeds of berries.",
    "Lettuce is a member of the sunflower family.",
    "Worcestershire sauce contains anchovies.",
    "An average ear of corn has 16 rows of kernels.",
    "Cashews grow on the bottom of cashew apples.",
    "Raspberries are made up of tiny individual drupelets.",
    "Gelatin is made from animal collagen.",
    "A cluster of bananas is called a \"hand,\" and each banana a \"finger.\"",
    "Mangoes are the most consumed fruit in the world.",
    "Avocados ripen faster next to bananas due to ethylene gas.",
    "A watermelon is both a fruit and a vegetable.",
    "McDonald's once made bubblegum-flavored broccoli.",
    "The world's largest pizza was over 13,500 square feet.",
    "Applesauce was the first food eaten in space.",
    "Oysters can change gender multiple times.",
    "Mustard is one of the oldest condiments known to humans.",
    "Garlic was once used as currency in ancient Egypt.",
    "Almonds are related to peaches.",
    "Seaweed is used in many ice creams as a stabilizer.",
    "A pineapple is not a single fruit, but a cluster of berries fused together.",
    "There are over 10,000 varieties of tomatoes.",
    "Rice is the staple food for more than half the world's population.",
    "Chili peppers trick your brain into thinking your mouth is on fire.",
    "The average person eats 35 tons of food in a lifetime.",
    "Bread is one of the oldest prepared foods, dating back at least 30,000 years.",
    "Popcorn is the oldest snack, dating back to 5,600 years ago.",
    "Almond milk has been around since the Middle Ages.",
    "The color of egg yolks depends on the hen's diet.",
    "Maple syrup is made from the sap of maple trees.",
    "Black pepper was once more valuable than gold.",
    "Brussels sprouts taste less bitter today due to selective breeding.",
    "Eating celery burns more calories than it provides."
  ], []);

  // Get today's tip with random selection and no repetition
  const getTodaysTip = useCallback(() => {
    const today = new Date().toDateString(); // Use date string as key
    const storageKey = 'prepright_tip_date';
    const usedTipsKey = 'prepright_used_tips';
    
    // Check if we already have a tip for today
    const lastTipDate = localStorage.getItem(storageKey);
    const lastTip = localStorage.getItem('prepright_last_tip');
    
    if (lastTipDate === today && lastTip) {
      return lastTip;
    }
    
    // Get used tips from localStorage
    let usedTipsList = JSON.parse(localStorage.getItem(usedTipsKey) || '[]');
    
    // If all tips have been used, reset the list
    if (usedTipsList.length >= cookingTips.length) {
      usedTipsList = [];
    }
    
    // Get available tips (not yet used)
    const availableTips = cookingTips.filter((_, index) => !usedTipsList.includes(index));
    
    // Pick a random tip from available ones
    const randomIndex = Math.floor(Math.random() * availableTips.length);
    const selectedTip = availableTips[randomIndex];
    const selectedTipIndex = cookingTips.indexOf(selectedTip);
    
    // Update used tips list
    usedTipsList.push(selectedTipIndex);
    
    // Save to localStorage
    localStorage.setItem(storageKey, today);
    localStorage.setItem('prepright_last_tip', selectedTip);
    localStorage.setItem(usedTipsKey, JSON.stringify(usedTipsList));
    
    return selectedTip;
  }, [cookingTips]);

  // Get today's fact with random selection and no repetition
  const getTodaysFact = useCallback(() => {
    const today = new Date().toDateString(); // Use date string as key
    const storageKey = 'prepright_fact_date';
    const usedFactsKey = 'prepright_used_facts';
    
    // Check if we already have a fact for today
    const lastFactDate = localStorage.getItem(storageKey);
    const lastFact = localStorage.getItem('prepright_last_fact');
    
    if (lastFactDate === today && lastFact) {
      return lastFact;
    }
    
    // Get used facts from localStorage
    let usedFactsList = JSON.parse(localStorage.getItem(usedFactsKey) || '[]');
    
    // If all facts have been used, reset the list
    if (usedFactsList.length >= foodFacts.length) {
      usedFactsList = [];
    }
    
    // Get available facts (not yet used)
    const availableFacts = foodFacts.filter((_, index) => !usedFactsList.includes(index));
    
    // Pick a random fact from available ones
    const randomIndex = Math.floor(Math.random() * availableFacts.length);
    const selectedFact = availableFacts[randomIndex];
    const selectedFactIndex = foodFacts.indexOf(selectedFact);
    
    // Update used facts list
    usedFactsList.push(selectedFactIndex);
    
    // Save to localStorage
    localStorage.setItem(storageKey, today);
    localStorage.setItem('prepright_last_fact', selectedFact);
    localStorage.setItem(usedFactsKey, JSON.stringify(usedFactsList));
    
    return selectedFact;
  }, [foodFacts]);

  // Custom success notification function
  const showSuccess = (message) => {
    // If an achievement is being shown, delay the success message
    if (isShowingAchievement) {
      setTimeout(() => {
        showSuccess(message);
      }, 6000); // Wait 6 seconds (5 for achievement + 1 buffer)
      return;
    }
    
    setSuccessMessage(message);
    setShowSuccessMessage(true);
    setTimeout(() => {
      setShowSuccessMessage(false);
    }, 3000);
  };

  // Calculate next bonus time
  const calculateNextBonusTime = () => {
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    
    const timeUntilMidnight = tomorrow.getTime() - now.getTime();
    const hours = Math.floor(timeUntilMidnight / (1000 * 60 * 60));
    const minutes = Math.floor((timeUntilMidnight % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  };

  // Function to check if activity was performed today (using backend API) - unused for now
  /*
  const wasActivityPerformedToday = async (activityType) => {
    try {
      // Map frontend activity types to backend activity types
      const activityTypeMap = {
        'meal_plan': 'meal_planned',
        'pantry_update': 'pantry_updated',
        'shopping_complete': 'shopping_completed',
        'shopping_create': 'shopping_list_created'
      };
      
      const backendActivityType = activityTypeMap[activityType];
      if (!backendActivityType) return false;
      
      // Check with backend API
      const response = await apiService.checkDailyPointsStatus(backendActivityType);
      return response.hasEarnedToday;
    } catch (error) {
      console.error('Failed to check daily points status:', error);
      // Fallback to localStorage if API fails
      const today = new Date().toDateString();
      const lastPerformed = localStorage.getItem(`prepright_${activityType}_last_performed`);
      return lastPerformed === today;
    }
  };
  */

  // Function to mark activity as performed today (using backend API) - unused for now
  /*
  const markActivityPerformedToday = async (activityType) => {
    try {
      // Map frontend activity types to backend activity types
      const activityTypeMap = {
        'meal_plan': 'meal_planned',
        'pantry_update': 'pantry_updated',
        'shopping_complete': 'shopping_completed',
        'shopping_create': 'shopping_list_created'
      };
      
      const backendActivityType = activityTypeMap[activityType];
      if (!backendActivityType) return;
      
      // The backend will handle the tracking when the activity is actually performed
      // We don't need to mark it here since the backend tracks it automatically
    } catch (error) {
      console.error('Failed to mark activity as performed:', error);
      // Fallback to localStorage if API fails
      const today = new Date().toDateString();
      localStorage.setItem(`prepright_${activityType}_last_performed`, today);
    }
  };
  */

  // Function to check task completion status
  const checkTaskCompletionStatus = async () => {
    try {
      // Map frontend task names to backend activity types
      const taskMapping = {
        meal_plan: 'meal_planned',
        pantry_update: 'pantry_updated',
        shopping_create: 'shopping_list_created',
        shopping_complete: 'shopping_completed',
        feed_post: 'feed_post_created',
        feed_comment: 'feed_comment_added',
        feed_like: 'feed_post_liked',
        sous_chef_chat: 'sous_chef_chat',
        profile_bio: 'profile_bio_updated',
        profile_picture: 'profile_picture_updated'
      };

      const statusChecks = await Promise.all(
        Object.entries(taskMapping).map(async ([taskKey, activityType]) => {
          try {
            const response = await apiService.checkDailyPointsStatus(activityType);
            return { taskKey, completed: response.hasEarnedToday };
          } catch (error) {
            console.error(`Failed to check status for ${taskKey}:`, error);
            return { taskKey, completed: false };
          }
        })
      );

      const newCompletedTasks = {};
      statusChecks.forEach(({ taskKey, completed }) => {
        newCompletedTasks[taskKey] = completed;
      });

      setCompletedTasks(newCompletedTasks);
    } catch (error) {
      console.error('Failed to check task completion status:', error);
    }
  };

  // Function to handle earn points action selection
  const handleEarnPointsAction = async () => {
    if (!selectedEarnActivity) {
      showSuccess('Please select an activity to earn points.');
      return;
    }

    // Navigate to the relevant page based on selection
    switch (selectedEarnActivity) {
      case 'meal_plan':
        navigate('/meal-plans');
        break;
      case 'pantry_update':
        navigate('/pantry');
        break;
      case 'shopping_complete':
        navigate('/shopping');
        break;
      case 'shopping_create':
        navigate('/shopping');
        break;
      case 'feed_post':
        navigate('/feed');
        break;
      case 'feed_comment':
        navigate('/feed');
        break;
      case 'feed_like':
        navigate('/feed');
        break;
      case 'sous_chef_chat':
        navigate('/assistant');
        break;
      case 'profile_bio':
        navigate('/profile');
        break;
      case 'profile_picture':
        navigate('/profile');
        break;
      case 'spin_wheel':
        navigate('/spin-wheel');
        break;
      default:
        showSuccess('Invalid activity selected.');
    }
    
    // Reset selection
    setSelectedEarnActivity('');
  };

  // Set today's tip and fact
  useEffect(() => {
    setTodaysTip(getTodaysTip());
    setTodaysFact(getTodaysFact());
  }, [getTodaysTip, getTodaysFact]);



  // Load user stats and leaderboard - simplified to prevent infinite loops
  useEffect(() => {
    const loadData = async () => {
      try {
        const leaderboardResponse = await apiService.getLeaderboard('all-time', 3);
        setLeaderboard(leaderboardResponse.leaderboard);

        if (!user || !apiService.getToken()) return;

        const [statsResponse, activeRewards, wheelStatus] = await Promise.all([
          apiService.getActivityStats(),
          apiService.getActiveRewards(),
          apiService.checkWheelStatus()
        ]);

        setStats(statsResponse.stats);

        // Check if user has active streak shield
        const hasShield = activeRewards.activeRewards?.some(
          reward => reward.rewardType === 'shield' && new Date(reward.expiresAt) > new Date()
        );
        setHasActiveShield(hasShield);
        
        // Check if user can spin the wheel today
        setCanSpinWheel(wheelStatus.canSpin);
        
        // Check completion status for all tasks
        await checkTaskCompletionStatus();
      } catch (error) {
        console.error('Failed to load dashboard data:', error);
      }
    };

    loadData();
  }, [user]); // Only depend on user

  // Refresh stats function that can be called after activities
  const refreshStats = useCallback(async () => {
    try {
      const leaderboardResponse = await apiService.getLeaderboard('all-time', 3);
      setLeaderboard(leaderboardResponse.leaderboard);

      if (!user || !apiService.getToken()) return;

      const statsResponse = await apiService.getActivityStats();
      setStats(statsResponse.stats);
    } catch (error) {
      console.error('Failed to refresh stats:', error);
    }
  }, [user]);

  // Listen for custom events to refresh stats
  useEffect(() => {
    const handleStatsRefresh = () => {
      refreshStats();
    };

    window.addEventListener('statsRefresh', handleStatsRefresh);
    return () => window.removeEventListener('statsRefresh', handleStatsRefresh);
  }, [refreshStats]);

  // Handle focus and visibility changes to refresh stats
  useEffect(() => {
    const handleFocus = () => {
      // Refresh stats when user returns to dashboard
      refreshStats();
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Refresh stats when tab becomes visible
        refreshStats();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refreshStats]);

  // Check if daily bonus has been claimed today
  useEffect(() => {
    const checkDailyBonus = async () => {
      try {
        const response = await apiService.getDailyBonusStatus();
        setDailyBonusClaimed(response.claimed);
        setNextBonusTime(calculateNextBonusTime());
      } catch (error) {
        console.error('Failed to check daily bonus status:', error);
        // Fallback to localStorage for offline mode
        const today = new Date().toDateString();
        const lastClaimed = localStorage.getItem('dailyBonusClaimed');
        setDailyBonusClaimed(lastClaimed === today);
        setNextBonusTime(calculateNextBonusTime());
      }
    };
    
    if (user && apiService.getToken()) {
      checkDailyBonus();
    }
  }, [user]);

  // Refresh daily bonus status when component becomes visible - DISABLED to prevent infinite loops
  useEffect(() => {
    // Temporarily disabled to prevent infinite loops
    // TODO: Re-enable with proper memoization when needed
    return () => {};
  }, [user]);

  // Update next bonus time every minute
  useEffect(() => {
    if (dailyBonusClaimed) {
      const timer = setInterval(() => {
        setNextBonusTime(calculateNextBonusTime());
      }, 60000); // Update every minute

      return () => clearInterval(timer);
    }
  }, [dailyBonusClaimed]);



  // Function to claim daily login bonus
  const claimDailyBonus = async () => {
    if (!user) {
      showSuccess('Please log in to claim your daily bonus.');
      return;
    }
    
    try {
      console.log('Attempting to claim daily bonus...');
      console.log('User:', user);
      console.log('Auth token:', apiService.getToken());
      
      const response = await apiService.logActivity({
        activityType: 'daily_login',
        description: 'Daily login bonus',
        pointsEarned: 5
      });
      
      // Handle achievement notifications
      if (response && handleApiResponse) {
        handleApiResponse(response);
      }
      
      console.log('Daily bonus response:', response);
      
      setStats(response.updatedStats);
      
      if (response.newAchievements.length > 0) {
        setNewAchievements(response.newAchievements);
        setTimeout(() => setNewAchievements([]), 5000);
      }
      
      await updateUser({
        streak: response.updatedStats.streak,
        points: response.updatedStats.points
      });
      
      // Mark daily bonus as claimed (backend handles the tracking)
      setDailyBonusClaimed(true);
      
      // Refresh task completion status
      await refreshStats();
      
      const multiplier = response.multiplier || 1;
      const basePoints = response.basePoints || 5;
      const finalPoints = response.finalPoints || 5;
      
      if (multiplier > 1) {
        showSuccess(`🎁 Daily bonus claimed! You earned ${finalPoints} points! (${basePoints} base + ${Math.round((multiplier - 1) * 100)}% streak bonus!)`);
      } else {
        showSuccess(`🎁 Daily bonus claimed! You earned ${finalPoints} points!`);
      }
    } catch (error) {
      console.error('Failed to claim daily bonus:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response,
        status: error.response?.status,
        data: error.response?.data
      });
      
      if (error.message === 'Daily bonus already claimed today') {
        showSuccess('🎁 Daily bonus already claimed today! Come back tomorrow for another bonus.');
        setDailyBonusClaimed(true);
      } else if (error.message.includes('already earned points for daily login today')) {
        showSuccess('🎁 Daily bonus already claimed today! Come back tomorrow for another bonus.');
        setDailyBonusClaimed(true);
        // Refresh the daily bonus status to ensure UI is in sync
        try {
          const response = await apiService.getDailyBonusStatus();
          setDailyBonusClaimed(response.claimed);
        } catch (refreshError) {
          console.error('Failed to refresh daily bonus status:', refreshError);
        }
      } else if (error.message.includes('already claimed')) {
        showSuccess('🎁 Daily bonus already claimed today! Come back tomorrow for another bonus.');
        setDailyBonusClaimed(true);
        // Refresh the daily bonus status to ensure UI is in sync
        try {
          const response = await apiService.getDailyBonusStatus();
          setDailyBonusClaimed(response.claimed);
        } catch (refreshError) {
          console.error('Failed to refresh daily bonus status:', refreshError);
        }
      } else {
        // Check for common error scenarios
        if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
          showSuccess('❌ Cannot connect to server. Please make sure the backend is running and try again.');
        } else if (error.message.includes('401') || error.message.includes('Unauthorized')) {
          showSuccess('❌ Authentication failed. Please log out and log back in.');
        } else if (error.message.includes('500')) {
          showSuccess('❌ Server error. Please try again later.');
        } else {
          showSuccess(`❌ Failed to claim bonus: ${error.message}`);
        }
      }
    }
  };

  
  return (
    <div className={`dashboard-container ${sidebarOpen ? 'sidebar-open' : ''}`}>
      {/* Top: Welcome */}
      <div className="welcome-row">
        <div>
          <h1 className="welcome-message">
            Welcome {user?.name || "Chef"} <span className="wave">👏</span>
          </h1>
          <p className="subtitle">Ready to cook something amazing today?</p>
        </div>
      </div>

      {/* Top row with level card + right stats */}
      <div className="top-row">
        <div className="level-card">
          <div className="level-info">
            <div className="avatar" style={{ backgroundColor: currentLevel.color }}>
              {user?.profilePicture ? (
                <img 
                  src={apiService.getImageUrl(user.profilePicture)} 
                  alt={`${user?.name || "User"}'s profile`}
                  className="profile-image"
                />
              ) : (
                user?.name?.charAt(0) || "C"
              )}
            </div>
            <div className="level-text">
              <h2>{currentLevel.name}</h2>
              <div className="meta-badges">
                <span className="badge">
                  🔥 {stats.streak} day streak
                  {hasActiveShield && <span className="shield-indicator" title="Streak Protected!">🛡️</span>}
                </span>
                <span className="badge">⭐ {stats.points} points</span>
              </div>
            </div>
          </div>

          <div className="level-bottom">
            <div className="progress-labels">
              <span>
                {progress.nextLevel ? `Progress to ${progress.nextLevel.name}` : 'Max Level Reached!'}
              </span>
              <span className="percent">{progress.percentage}%</span>
            </div>
            <div className="progress-bar">
              <div 
                className="progress" 
                style={{ 
                  width: `${progress.percentage}%`,
                  backgroundColor: currentLevel.color 
                }}
              ></div>
            </div>
            {progress.nextLevel && (
              <div className="points-to-next">
                Current level {currentLevel.level} ({progress.pointsToNext} points to next level)
              </div>
            )}
          </div>
        </div>


        <div className="stats-column">
          {/* Daily Bonus Box */}
          <div 
            className={`stat-card daily-bonus-card ${dailyBonusClaimed ? 'claimed' : ''}`} 
            onClick={dailyBonusClaimed ? null : claimDailyBonus} 
            style={{ cursor: dailyBonusClaimed ? 'default' : 'pointer' }}
          >
            <div className="bonus-icon">🎁</div>
            <div className="bonus-content">
              <h4>{dailyBonusClaimed ? 'Bonus Claimed!' : 'Daily Bonus'}</h4>
              <p>
                {dailyBonusClaimed 
                  ? `Next bonus in ${nextBonusTime}`
                  : 'Claim your daily 5 points!'
                }
              </p>
            </div>
          </div>

          {/* Earn Points Box */}
          <div className="stat-card earn-points-card">
            <div className="earn-points-header">
              <h4>Earn Points</h4>
            </div>
            <div className="earn-points-content">
              <select 
                value={selectedEarnActivity} 
                onChange={(e) => setSelectedEarnActivity(e.target.value)}
                className="earn-points-dropdown"
              >
                <option value="">Select activity...</option>
                {!completedTasks.meal_plan && <option value="meal_plan">📅 Plan Meal (+15 points)</option>}
                {!completedTasks.pantry_update && <option value="pantry_update">🛒 Update Pantry (+10 points)</option>}
                {!completedTasks.shopping_create && <option value="shopping_create">📝 Create Shopping List (+5 points)</option>}
                {!completedTasks.shopping_complete && <option value="shopping_complete">🛍️ Complete Shopping (+20 points)</option>}
                {!completedTasks.feed_post && <option value="feed_post">📝 Create Feed Post (+12 points)</option>}
                {!completedTasks.feed_comment && <option value="feed_comment">💬 Comment on Post (+6 points)</option>}
                {!completedTasks.feed_like && <option value="feed_like">❤️ Like a Post (+3 points)</option>}
                {!completedTasks.sous_chef_chat && <option value="sous_chef_chat">👨‍🍳 Talk to Sous-chef (+10 points)</option>}
                {!completedTasks.profile_bio && <option value="profile_bio">👤 Edit Profile Bio (+8 points)</option>}
                {!completedTasks.profile_picture && <option value="profile_picture">📸 Upload Profile Picture (+12 points)</option>}
                {canSpinWheel && <option value="spin_wheel">🎡 Spin the Wheel (Daily Luck!)</option>}
              </select>
              <button 
                onClick={handleEarnPointsAction}
                disabled={!selectedEarnActivity}
                className="earn-points-button"
              >
                Go to Activity
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Achievement Notifications */}
      {newAchievements.length > 0 && (
        <div className="achievement-notification">
          <div className="notification-content">
            <span className="notification-icon">🎉</span>
            <div className="notification-text">
              <strong>New Achievement{newAchievements.length > 1 ? 's' : ''} Unlocked!</strong>
              <span>{newAchievements.map(a => a.name).join(', ')}</span>
            </div>
            <button 
              className="notification-close"
              onClick={() => setNewAchievements([])}
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Quick Actions heading */}
      <h3 className="section-title">Quick Actions</h3>

      {/* Quick Actions */}
      <div className="quick-actions">
        <div className="action-card" onClick={() => navigate('/recipes')} style={{ cursor: 'pointer' }}>
          <div className="action-icon green">🍽️</div>
          <h4>Recipes</h4>
          <p>Browse and save your favorite recipes</p>
        </div>

        <div className="action-card" onClick={() => navigate('/pantry')} style={{ cursor: 'pointer' }}>
          <div className="action-icon blue">🛒</div>
          <h4>Pantry</h4>
          <p>Track your ingredients and expiry dates</p>
        </div>

        <div className="action-card" onClick={() => navigate('/shopping')} style={{ cursor: 'pointer' }}>
          <div className="action-icon orange">📝</div>
          <h4>Shopping List</h4>
          <p>Create and manage your shopping lists</p>
        </div>

        <div className="action-card" onClick={() => navigate('/meal-plans')} style={{ cursor: 'pointer' }}>
          <div className="action-icon purple">📅</div>
          <h4>Meal Plans</h4>
          <p>Plan your weekly meals and schedule</p>
        </div>
      </div>

      {/* Bottom row with Leaderboard and Tips/Facts */}
      <div className="bottom-row">
        <div className="leaderboard-box">
          <h3 className="section-title">🏆 Leaderboard</h3>
          <div className="leaderboard-content">
            {leaderboard.length > 0 ? (
              leaderboard.map((player, index) => (
                <div key={player.id} className={`leaderboard-item ${index === 0 ? 'first' : index === 1 ? 'second' : 'third'}`}>
                  <div className="rank-badge">
                    {index + 1}
                  </div>
                  <div className="player-info">
                    <div className="player-name">{player.name}</div>
                    <div className="player-stats">
                      <span className="player-level">{calculateLevel(player.points || 0).name}</span>
                      <span className="player-points">{player.points} pts</span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="no-leaderboard">No players yet. Be the first to earn points!</p>
            )}
          </div>
        </div>

        <div className="tips-facts-row">
          <div className="tip-box">
            <h4>💡 Today's Tip</h4>
            <p>{todaysTip}</p>
          </div>
          <div className="fact-box">
            <h4>🤓 Fun Fact</h4>
            <p>{todaysFact}</p>
          </div>
        </div>
      </div>

      {/* Custom Success Notification */}
      {showSuccessMessage && (
        <div className="success-notification">
          <div className="success-content">
            <span className="success-icon">✅</span>
            <div className="success-text">
              <strong>Success!</strong>
              <span>{successMessage}</span>
            </div>
            <button className="success-close" onClick={() => setShowSuccessMessage(false)}>×</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
